<template>
  <footer v-once :class="classes">
    <nav class="main-footer__content">
      <ul class="main-footer__menu">
        <li
          v-for="item in menu"
          :key="item.key"
          :class="menuItemClass(item.key)"
        >
          <a
            :href="item.url"
            data-track-id="footer"
            class="main-footer__link"
            target="_blank"
            rel="noopener"
            data-test-id="main-footer-link"
          >
            {{ $t(`common.footer.${item.key}`) }}
          </a>
        </li>
      </ul>
      <ul class="main-footer__menu main-footer__menu--social">
        <li
          v-for="item in socialMenu"
          :key="item.key"
          class="main-footer__menu-item"
        >
          <a
            :href="item.url"
            data-track-id="footer"
            class="main-footer__link"
            rel="noreferrer"
            :title="item.label"
            data-test-id="main-footer-social-link"
          >
            <EIcon :icon="item.label" size="s" class="main-footer__social-icon" />
          </a>
        </li>
      </ul>
    </nav>
  </footer>
</template>

<script>
import { mapState } from 'vuex';

import { getFooterMenu, getSocialMenu as socialMenu } from '~~/common/js/universal/menus';
import classes, { getModifierClass } from '~~/common/vue/mixins/classes';
import EIcon from '~~/common/vue/components/icon';

export const alignments = ['left', 'center'];

export default {
  name: 'EMainFooter',
  components: {
    EIcon,
  },
  mixins: [classes],
  props: {
    settingsLink: {
      type: Boolean,
      default: false,
    },
    align: {
      type: String,
      default: 'left',
      validator: (alignment) => alignments.includes(alignment),
      cssClass: true,
    },
  },
  computed: {
    ...mapState('global', ['locale']),
    menu() {
      return getFooterMenu(this.locale, this.settingsLink);
    },
    socialMenu,
  },
  methods: {
    menuItemClass(key) {
      const baseClass = 'main-footer__menu-item';
      return [baseClass, getModifierClass(baseClass, key)];
    },
  },
};
</script>

<style lang="scss" scoped>
.main-footer__menu-item {
  line-height: 1em; //TODO: cleanup footer line-height and vertical alignments
  margin-bottom: $space-s;
  padding: 0 $space-s;

  @include tablet {
    margin-bottom: 0;

    &:first-of-type {
      padding-left: 0;
    }

    &:last-of-type {
      padding-right: 0;
    }
  }
}

.main-footer {
  @include divider('top');

  font-size: $font-m;
  color: $color-text-secondary;
  background-color: $gray-10;
  padding: $space-m $space-4l;
  width: 100%;

  @include tablet {
    font-size: $font-s;
    padding: $space-s $space-s;
  }

  @include desktop {
    border-top: none;
    // non-standard spacing here specified in design
    padding: #{$space-s - 1px} 0;
  }

  &__content {
    max-width: 1082px;
    text-align: center;
    width: 100%;

    @include desktop {
      display: flex;
      margin: 0;
      position: relative;
      text-align: left;
    }
  }

  &__link {
    color: inherit;
    transition: color $timing-2s $easing;

    &:hover,
    &:focus {
      color: $gray-50;
    }

    &:first-of-type {
      padding-left: 0;
    }
  }

  &--align-center {
    display: flex;
    justify-content: center;

    .main-footer__content {
      justify-content: center;
    }
  }

  &--align-left {
    .main-footer__content {
      @include desktop {
        padding-left: $page-container-left-space-desktop;
      }
    }

    .main-footer__menu-item {
      &--feedback {
        @include desktop {
          position: absolute;
          right: $space-l;
        }
      }

      @include desktop {
        &:nth-last-of-type(2) {
          padding-right: 0;
        }
      }
    }
  }
}

// overriding the default FeedbackLink hover state
::v-deep .main-footer__link--feedback .link {
  &:hover {
    text-decoration: none;
    color: $gray-50;
  }
}

.main-footer__menu {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  list-style-type: none;
  margin: 0;
  padding: 0;

  @include tablet {
    align-items: center;
    display: inline-flex;
  }
}

.main-footer__menu--social {
  margin-top: $space-1s;

  @include tablet {
    margin-top: 0;

    &::before {
      @include divider('left');

      content: "";
      display: inline-block;
      width: 1px;
      height: $space-s;
      margin: 0 $space-1l 0 $space-1l;
    }
  }

  .main-footer__menu-item {
    margin-bottom: 0;
    padding: 0;

    & + .main-footer__menu-item {
      margin-left: $space-1s;

      @include desktop {
        margin-left: $space-s;
      }
    }
  }
}

.main-footer__social-icon {
  @include desktop {
    width: 18px;
  }
}
</style>

<docs>
```
<EMainFooter />
```
</docs>
